import { createSelector } from "@reduxjs/toolkit";
import { withinCallCenterHours } from "utils/time/withinCallCenterHours";

import {
	getFacilityWithUnits,
	getFacilityCardProps
} from "store/selectors/entities/facility";
import formatPageTypeTitle from "utils/search/formatPageTypeTitle";
import buildSisterPages from "utils/search/buildSisterPages";
// Search Page Meta

export const selectSearchPageMeta = (state) => ({
	cityUrl: state.search.cityUrl,
	meta: {
		...state.search.meta,
		canonical: state.app.baseUrl + state.search.meta.canonical
	},
	pageText: state.search.pageText,
	nearbyCities: state.search.nearbyCities,
	location: state.search.location,
	pageType: state.search.pageType,
	headline:
		state.search.headline || formatPageTypeTitle(state.search.subPageType),
	stateUrl: state.search.stateUrl,
	staticMapUrl: state.search.staticMapUrl
});

export const selectSearchResultsPagination = (state) => ({
	currentPage: state.search.page,
	hasMorePages: state.search.hasMorePages,
	totalListings: state.search.totalListings
});

export const selectSisterPages = (state) => ({
	amenityPages: buildSisterPages(
		state.search.amenityPages,
		state.search.meta.canonicalUri
	)
});

// Uses state.search.facilities (IDs for current search) so results replace on new search (e.g. map move), not append
export const selectFacilityCards = createSelector(
	({ search }) => (search && (search.facilities || search.listings)) || [],
	({ facilities }) => facilities.byId,
	({ units }) => units.byId,
	({ app }) => app?.baseUrl || "",
	(facilityIds, facilitiesById, unitsById, baseUrl) => {
		const ids = Array.isArray(facilityIds) ? facilityIds : [];
		const cards = ids
			.map((id) => getFacilityWithUnits(id, facilitiesById, unitsById))
			.map((f) => getFacilityCardProps(f))
			.filter((f) => f);

		if (!baseUrl) {
			return cards;
		}

		return cards.map((card) => {
			if (!card.schema || !card.schema.url) {
				return card;
			}

			const hasAbsoluteUrl = /^https?:\/\//i.test(card.schema.url);
			if (hasAbsoluteUrl) {
				return card;
			}

			return {
				...card,
				schema: {
					...card.schema,
					url: `${baseUrl}${card.schema.url}`
				}
			};
		});
	}
);

// Derived, environment-aware value. Returns null on SSR to avoid mismatches.
export const selectCallCenterOpen = () =>
	typeof window !== "undefined" ? withinCallCenterHours() : null;

export const searchPageSelector = createSelector(
	selectSearchPageMeta,
	selectSearchResultsPagination,
	selectSisterPages,
	selectFacilityCards,
	selectCallCenterOpen,
	(
		searchPageMeta,
		searchResultsPagination,
		sisterPages,
		facilityCards,
		isCallCenterOpen
	) => ({
		...searchPageMeta,
		...searchResultsPagination,
		...sisterPages,
		facilityCards,
		isCallCenterOpen
	})
);

export default searchPageSelector;
